import i18next from 'i18next';
import {formValueSelector} from 'redux-form';

import createSelector from 'web-app/util/custom-create-selector';
import {type RootState} from 'web-app/react/redux/main-reducer';
import * as MomentUtil from 'web-app/util/moment';
import PlatformHelper from 'web-app/helpers/platform-helper';
import LocalStorageService from 'web-app/services/local-storage';
import {exhaustiveCheck, hasValue} from 'web-app/util/typescript';

import * as Constants from './constants';
import {type LoginSelectorOption, fieldNames} from './models';
import * as Helpers from './helpers';

const loginFormSelector = formValueSelector(Constants.FORM_NAMES.LOGIN_FORM);
export const loginEmail = (state: RootState) => loginFormSelector(state, fieldNames('email'));

const selectedAccountId = (state: RootState) => state.login.selectedAccountId;
const deviceAccounts = (state: RootState) => state.login.deviceAccounts;

const selectedAccount = createSelector(deviceAccounts, selectedAccountId, (accounts, accountId) =>
    accounts.find(account => account.loginId === accountId),
);

export const hasSavedAccounts = createSelector(
    (state: RootState) => state.login.deviceAccounts,
    accounts => accounts.size > 0,
);

export const accountsForCarousel = createSelector(
    (state: RootState) => state.login.deviceAccounts,
    accounts =>
        accounts.size === 0
            ? null
            : accounts
                  .map(account => ({
                      image: account.image,
                      id: account.loginId,
                      title: account.name.fullName,
                      subtitle: `${i18next.t('lastActive')} ${MomentUtil.fromNow(account.lastLogin, true)}`,
                      secret: undefined,
                  }))
                  .push({
                      image: '',
                      id: Constants.NEW_ACCOUNT_IDENTIFIER,
                      title: i18next.t('login.addAccount'),
                      subtitle: '',
                      secret: undefined,
                  })
                  .toArray(),
);

const possibleLogins = createSelector(
    (state: RootState) => state.login.existingLogins,
    logins => logins.filter(login => login.exists),
);

export const getOneTimePasswordConfiguration = createSelector(
    possibleLogins,
    (state: RootState, {scope}: {scope: 'login' | 'forgot-password'}) => scope,
    (possibleLogins, scope) => {
        const loginWithCanUseOneTimePassword = possibleLogins.find(login => {
            switch (scope) {
                case 'login':
                    return Boolean(login.canUseOneTimePassword && !login.passwordLoginAvailable);
                case 'forgot-password':
                    return Boolean(login.canUseOneTimePassword);
                default:
                    exhaustiveCheck(scope);
                    return false;
            }
        });

        if (!loginWithCanUseOneTimePassword) {
            return {canUseOneTimePassword: false, apiKey: null};
        }

        return {
            canUseOneTimePassword: true,
            apiKey: Helpers.getTLD(loginWithCanUseOneTimePassword.baseUrl),
        };
    },
);

export const externalLogins = createSelector(possibleLogins, possibleLogins =>
    possibleLogins.filter(login => !login.local),
);

export const showLoginOptions = createSelector(possibleLogins, externalLogins, (possibleLogins, externalLogins) =>
    PlatformHelper.isMobileApp() ? possibleLogins.size > 1 : externalLogins.size > 0,
);

export const openIdProviders = createSelector(possibleLogins, possibleLogins => {
    return possibleLogins.flatMap(domain => {
        return (
            domain.useOpenIdConnect?.map(openIdProvider => {
                return {
                    ...openIdProvider,
                    selectedApiKey: Helpers.getTLD(domain.baseUrl),
                };
            }) ?? []
        );
    });
});

export const loginSelectorOptions = createSelector(possibleLogins, possibleLogins =>
    possibleLogins.size > 1
        ? possibleLogins
              .map(
                  (login): LoginSelectorOption => ({
                      label: login.baseUrl,
                      value: login.baseUrl,
                      local: login.local,
                  }),
              )
              .toArray()
        : undefined,
);

export const emailForSelectedAccount = createSelector(selectedAccount, account => account && account.email);

export const nameForSelectedAccount = createSelector(selectedAccount, account => account && account.name.fullName);

export const shouldUseUserContextForSelectedAccount = createSelector(selectedAccount, account =>
    Boolean(account?.useUserContextAuthentication),
);

const internalLogins = createSelector(possibleLogins, possibleLogins => possibleLogins.filter(login => login.local));

const canLoginLocally = createSelector(internalLogins, internalLogins => internalLogins.size > 0);

export const disableLoginForm = createSelector(
    canLoginLocally,
    showLoginOptions,
    (canLoginLocally, showLoginOptions) => {
        return !PlatformHelper.isMobileApp() && !canLoginLocally && showLoginOptions;
    },
);

export const initialLoginFormEmailValue = createSelector(
    (_, props: {email?: string}) => props.email,
    email => email || LocalStorageService.getFromLocalStorage('email'),
);

export const initialLoginFormValues = createSelector(initialLoginFormEmailValue, email => ({
    email,
}));

export const initialForgotPasswordFormValues = createSelector(
    (_, props) => props.email,
    email => ({
        email,
    }),
);
export const shouldShowError = createSelector(
    (state: RootState) => state.login.errorState,
    errorState => Boolean(errorState),
);

export const locale = createSelector(
    (state: RootState, props: {locale?: string}) => props.locale,
    locale => locale || LocalStorageService.getFromLocalStorage('locale'),
);

export const showMagicLinkOption = createSelector(
    (state: RootState) => state.login.existingLogins,
    logins =>
        logins &&
        logins.size === 1 &&
        logins.every(
            login =>
                login.exists &&
                !login.passwordLoginAvailable &&
                login.passwordlessLoginAvailable &&
                !login.canUseOneTimePassword,
        ),
);

export const loginForMagicLink = createSelector(
    showMagicLinkOption,
    (state: RootState) => state.login.existingLogins,
    (showMagicLinkOption, logins) => {
        if (showMagicLinkOption) {
            return logins.first();
        }
        return null;
    },
);

export const cannotLoginWithPassword = createSelector(
    (state: RootState) => state.login.existingLogins,
    logins => {
        return logins?.size > 0 && logins.every(login => !login.passwordLoginAvailable && !login.canUseOneTimePassword);
    },
);

export const canResetPassword = createSelector(
    (state: RootState) => state.login.existingLogins,
    logins => {
        // If we haven't yet fetched existing logins or no logins were found,
        // we should allow the user the option to request new password.
        if (!hasValue(logins) || logins.size === 0) {
            return true;
        }

        // If we have existing login(s), then let use the flag to decide.
        // If any of the found accounts allow it, then show the option to request new password.
        return logins.some(login => Boolean(login.canResetPassword));
    },
);

export const magicLinkSent = createSelector(
    (state: RootState) => state.login.magicLinkSent,
    magicLinkSent => magicLinkSent,
);

export const shouldUseUserContext = createSelector(
    (state: RootState) => state.login.existingLogins,
    logins => {
        if (!logins) {
            return false;
        }

        return Boolean(logins.first()?.useUserContextAuthentication);
    },
);

export const requestOpenIdConnectTokenInfo = createSelector(
    (state: RootState) => state.login.existingLogins,
    logins => {
        return logins
            .map(login => {
                if (!login.canRequestOpenIdConnectToken) {
                    return undefined;
                }

                return {
                    selectedApiKey: Helpers.getTLD(login.baseUrl),
                };
            })
            .filter(hasValue)
            .toArray();
    },
);
